/**
 * ResearchEnrichment — admin-only tool for enriching a CSV of preloaded
 * food trucks with contact info + outreach method recommendations.
 *
 * Flow:
 *   1. Admin drops or picks a CSV (uses the preloaded-trucks importer
 *      schema — `truck_name`, `city`, `state`, `email`, etc).
 *   2. Backend parses + kicks off a background enrichment job that
 *      runs one GPT-5.5 call per truck (bounded concurrency = 3).
 *   3. Frontend polls the job every 2s, showing a progress bar +
 *      the results grid as rows land.
 *   4. Admin filters by research_status, edits inline or rejects
 *      whole rows, then downloads either the enriched TruckTrackr
 *      importer CSV or the outreach CRM XLSX.
 *
 * MVP notes (Feb 2026):
 *   - No per-field accept/reject grid — full row edit + row reject
 *     is enough for the first Philadelphia 51-75 batch.
 *   - No direct "push into preloaded_trucks" button — admin downloads
 *     the enriched CSV and re-imports via the existing importer.
 *   - Every URL/email field renders a "confidence" pill so admins
 *     know what to verify.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BACKEND_BASE } from "../../lib/backendBase";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  UploadSimple, MagnifyingGlass, DownloadSimple, Trash, Check, X,
  Warning, EnvelopeSimple, InstagramLogo, FacebookLogo, Globe, Phone,
  CaretRight,
} from "@phosphor-icons/react";

const FILTER_CHIPS = [
  { key: "all", label: "All" },
  { key: "email_found", label: "Email Found" },
  { key: "contact_form_only", label: "Contact Form" },
  { key: "social_only", label: "Social Only" },
  { key: "needs_review", label: "Needs Review" },
  { key: "possibly_inactive", label: "Possibly Inactive" },
];

function ConfidencePill({ conf }) {
  const c = (conf || "low").toLowerCase();
  const cls =
    c === "high" ? "bg-emerald-100 text-emerald-800" :
    c === "medium" ? "bg-amber-100 text-amber-800" :
    "bg-neutral-100 text-neutral-500";
  return (
    <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded ${cls}`}>
      {c}
    </span>
  );
}

/** Small inline-editable input. `onCommit` fires on blur or Enter,
 *  never on every keystroke so the PATCH endpoint isn't hammered. */
function InlineField({ value, placeholder, onCommit, testId }) {
  const [v, setV] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(value || ""), [value]);
  const commit = async () => {
    if ((v || "") === (value || "")) return;
    setSaving(true);
    try { await onCommit(v); } finally { setSaving(false); }
  };
  return (
    <input
      type="text"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
      placeholder={placeholder}
      disabled={saving}
      className="w-full bg-transparent border-b border-transparent hover:border-border focus:border-primary outline-none text-xs px-1 py-0.5 disabled:opacity-50"
      data-testid={testId}
    />
  );
}

function ResultRow({ row, onPatch }) {
  const cpf = row.confidence_per_field || {};
  const patch = (field, value) => onPatch(row.id, { [field]: value });

  return (
    <tr className={row.rejected ? "opacity-40 bg-muted/30" : ""} data-testid={`research-row-${row.id}`}>
      <td className="px-2 py-2 align-top w-8">
        <button
          type="button"
          onClick={() => patch("rejected", !row.rejected)}
          className="text-neutral-400 hover:text-red-500"
          title={row.rejected ? "Un-reject" : "Reject row"}
          data-testid={`research-reject-${row.id}`}
        >
          {row.rejected ? <Check className="h-4 w-4 text-emerald-500" /> : <X className="h-4 w-4" />}
        </button>
      </td>
      <td className="px-2 py-2 align-top">
        <div className="font-semibold text-sm text-foreground">{row.truck_name}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
          {row.city}{row.state ? `, ${row.state}` : ""} · {row.cuisine_type || "—"}
        </div>
        {row.notes && (
          <div className="text-[10px] text-muted-foreground mt-1 italic line-clamp-2" title={row.notes}>
            {row.notes}
          </div>
        )}
      </td>
      <td className="px-2 py-2 align-top">
        <div className="flex items-center gap-1">
          <EnvelopeSimple className="h-3 w-3 text-muted-foreground flex-none" />
          <InlineField
            value={row.email}
            placeholder="—"
            onCommit={(v) => patch("email", v)}
            testId={`research-email-${row.id}`}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <ConfidencePill conf={cpf.email} />
          {row.hunter?.confidence != null && (
            <span
              className="inline-block text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-sky-100 text-sky-800"
              title={`Hunter.io found ${row.hunter.found_count || 0} email${row.hunter.found_count === 1 ? "" : "s"} on ${row.hunter.domain} · confidence ${row.hunter.confidence}%`}
            >
              hunter {row.hunter.confidence}%
            </span>
          )}
        </div>
      </td>
      <td className="px-2 py-2 align-top">
        <div className="flex items-center gap-1">
          <Globe className="h-3 w-3 text-muted-foreground flex-none" />
          <InlineField
            value={row.website_url}
            placeholder="—"
            onCommit={(v) => patch("website_url", v)}
            testId={`research-website-${row.id}`}
          />
        </div>
        <ConfidencePill conf={cpf.website_url} />
      </td>
      <td className="px-2 py-2 align-top">
        <div className="flex items-center gap-1">
          <InstagramLogo className="h-3 w-3 text-muted-foreground flex-none" />
          <InlineField
            value={row.instagram_url}
            placeholder="—"
            onCommit={(v) => patch("instagram_url", v)}
            testId={`research-ig-${row.id}`}
          />
        </div>
        <div className="flex items-center gap-1">
          <FacebookLogo className="h-3 w-3 text-muted-foreground flex-none" />
          <InlineField
            value={row.facebook_url}
            placeholder="—"
            onCommit={(v) => patch("facebook_url", v)}
            testId={`research-fb-${row.id}`}
          />
        </div>
      </td>
      <td className="px-2 py-2 align-top">
        <div className="flex items-center gap-1">
          <Phone className="h-3 w-3 text-muted-foreground flex-none" />
          <InlineField
            value={row.phone}
            placeholder="—"
            onCommit={(v) => patch("phone", v)}
            testId={`research-phone-${row.id}`}
          />
        </div>
      </td>
      <td className="px-2 py-2 align-top">
        <div className="text-[11px] font-semibold text-foreground">
          {row.best_contact_method || "—"}
        </div>
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">
          {row.research_status || "—"}
        </div>
      </td>
    </tr>
  );
}

export default function ResearchEnrichment() {
  const { user, loading: authLoading } = useAuth();
  const [job, setJob] = useState(null);        // {id, status, progress, ...}
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [recentJobs, setRecentJobs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("full");     // "full" | "hunter_only"
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const loadJobList = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/research/jobs");
      setRecentJobs(data || []);
    } catch (e) { console.warn("research: couldn't load recent jobs", e); }
  }, []);

  const loadJob = useCallback(async (jobId) => {
    if (!jobId) return;
    try {
      const { data } = await api.get(`/admin/research/jobs/${jobId}`);
      setJob(data.job);
      setRows(data.rows || []);
      setCounts(data.counts || {});
      // Stop polling once the job is terminal.
      if (data.job.status !== "running" && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load job"));
    }
  }, []);

  useEffect(() => { loadJobList(); }, [loadJobList]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPolling = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadJob(jobId), 2000);
    loadJob(jobId);
  }, [loadJob]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post("/admin/research/jobs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Started ${mode === "hunter_only" ? "Hunter-only" : "full"} enrichment for ${data.row_count} trucks${data.city_hint ? ` (${data.city_hint})` : ""}`);
      startPolling(data.job_id);
      loadJobList();
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const patchRow = useCallback(async (rowId, changes) => {
    // Optimistic — flip local state immediately so inline edits feel snappy.
    setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, ...changes } : r)));
    try {
      await api.patch(`/admin/research/jobs/${job.id}/rows/${rowId}`, changes);
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
      loadJob(job.id);   // reload to reconcile after failure
    }
  }, [job, loadJob]);

  const downloadCsv = () => {
    if (!job) return;
    // Use the standard href-download pattern — bypasses api.js so
    // the browser handles the response as a file save dialog rather
    // than JSON parsing.
    const url = `${BACKEND_BASE}/api/admin/research/jobs/${job.id}/export.csv`;
    fetchAndDownload(url, `trucktrackr-enriched-${job.id.slice(0, 8)}.csv`);
  };
  const downloadXlsx = () => {
    if (!job) return;
    const url = `${BACKEND_BASE}/api/admin/research/jobs/${job.id}/crm.xlsx`;
    fetchAndDownload(url, `outreach-crm-${job.id.slice(0, 8)}.xlsx`);
  };

  /** Auth-preserving download — plain <a href> would drop the JWT
   *  header, so we fetch with credentials + turn the blob into a
   *  synthetic anchor click. */
  const fetchAndDownload = async (url, filename) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error(`Download failed: ${e?.message || e}`);
    }
  };

  const deleteJob = async () => {
    if (!job) return;
    if (!window.confirm(`Delete this enrichment job and all ${rows.length} enriched rows?`)) return;
    try {
      await api.delete(`/admin/research/jobs/${job.id}`);
      setJob(null); setRows([]); setCounts({});
      if (pollRef.current) clearInterval(pollRef.current);
      loadJobList();
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed"));
    }
  };

  const visibleRows = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => (r.research_status || "needs_review") === filter);
  }, [rows, filter]);

  const isRunning = job?.status === "running";
  const pct = job?.progress_pct || 0;

  // Auth gate — placed AFTER all hooks so we don't violate Rules of
  // Hooks by early-returning above `useState` / `useEffect` calls.
  if (!authLoading && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="research-enrichment-page">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <header className="space-y-2">
          <Link to="/admin" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
            <CaretRight weight="bold" className="h-3 w-3 rotate-180" /> Back to admin
          </Link>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
            <MagnifyingGlass weight="duotone" className="h-7 w-7 text-primary" />
            Research &amp; Contact Enrichment
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Upload a CSV of preloaded trucks; the tool researches each row and returns a
            starting-point record with best-guess website, email, socials, phone, and a
            recommended contact method. Every field is inline-editable — verify what looks
            weak, reject junk rows, then download the enriched importer CSV or outreach CRM.
          </p>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 max-w-3xl">
            <strong>How it works:</strong> Two-pass enrichment. First, GPT-5.5 reasons about
            each truck (guesses likely IG handles, recommends contact methods, flags dead trucks
            based on training-data recall). Then <strong>Hunter.io</strong> does a live-web
            domain lookup for any truck with a website URL — real emails scraped from real pages
            with a numeric confidence score. Fields tagged <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-sky-100 text-sky-800">hunter&nbsp;90%</span>
            are live-verified. LLM-only guesses cap at &ldquo;medium&rdquo; — always verify those.
          </div>
        </header>

        {/* Upload card */}
        <section className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">1. Upload a CSV</div>
              <div className="text-xs text-muted-foreground">
                Required column: <code className="text-[10px]">truck_name</code>. Optional:
                {" "}<code className="text-[10px]">city, state, cuisine_type, website_url, instagram_url, facebook_url, email, phone, source_notes</code>.
                Existing values pass through untouched.
              </div>
            </div>
            <label className="inline-block">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onUpload}
                className="hidden"
                data-testid="research-upload-input"
                disabled={uploading || isRunning}
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || isRunning}
                className="gap-2"
                data-testid="research-upload-btn"
              >
                <UploadSimple weight="bold" className="h-4 w-4" />
                {uploading ? "Uploading…" : isRunning ? "Job in progress…" : "Upload CSV"}
              </Button>
            </label>
          </div>

          {/* Mode toggle — placed under the upload button so admins
              see both options before clicking Upload. */}
          <div className="pt-2 border-t border-border">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2">
              Enrichment mode
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setMode("full")}
                disabled={uploading || isRunning}
                className={`flex-1 text-left rounded-lg border p-3 transition-colors ${
                  mode === "full"
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-primary/40"
                } disabled:opacity-50`}
                data-testid="research-mode-full"
              >
                <div className="font-semibold text-sm">Full research (LLM + Hunter)</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  GPT-5.2 guesses socials + contact method for every truck, then
                  Hunter.io does a live-web email lookup on any website URL.
                  ~5s per truck. Uses LLM credits.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode("hunter_only")}
                disabled={uploading || isRunning}
                className={`flex-1 text-left rounded-lg border p-3 transition-colors ${
                  mode === "hunter_only"
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-primary/40"
                } disabled:opacity-50`}
                data-testid="research-mode-hunter-only"
              >
                <div className="font-semibold text-sm">Hunter-only fast lane</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Skip the LLM step. Runs Hunter.io only on rows that already
                  have a website URL. ~1s per truck, zero LLM cost. Best for
                  follow-up passes on lists you&rsquo;ve already researched.
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* Job status + progress */}
        {job && (
          <section className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-3" data-testid="research-job-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  Job {job.id.slice(0, 8)}
                  {job.city_hint ? ` · ${job.city_hint}` : ""}
                </div>
                <div className="text-sm font-semibold">
                  {job.filename || "upload.csv"} · {job.row_count} trucks
                  {job.mode === "hunter_only" && (
                    <span className="ml-2 text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded align-middle">
                      hunter-only
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                    job.status === "running"
                      ? "bg-primary/10 text-primary"
                      : job.status === "completed"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-red-100 text-red-800"
                  }`}
                  data-testid="research-job-status"
                >
                  {job.status}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadCsv}
                  disabled={rows.length === 0}
                  data-testid="research-download-csv"
                >
                  <DownloadSimple weight="bold" className="h-3.5 w-3.5 mr-1" />
                  Enriched CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadXlsx}
                  disabled={rows.length === 0}
                  data-testid="research-download-xlsx"
                >
                  <DownloadSimple weight="bold" className="h-3.5 w-3.5 mr-1" />
                  CRM (.xlsx)
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={deleteJob}
                  data-testid="research-delete-job"
                >
                  <Trash weight="bold" className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{job.progress || 0} / {job.row_count} enriched</span>
                <span className="flex items-center gap-2">
                  {isRunning && job.progress > 0 && (() => {
                    // Rough ETA: extrapolate from elapsed time so far.
                    // Not perfect (concurrency batches finish in bursts)
                    // but good enough to answer "is it stuck or moving?".
                    try {
                      const startMs = new Date(job.created_at).getTime();
                      const elapsedS = (Date.now() - startMs) / 1000;
                      const perRow = elapsedS / job.progress;
                      const remaining = Math.max(0, job.row_count - job.progress);
                      const etaS = Math.round(remaining * perRow);
                      if (etaS > 0) {
                        return <span className="text-primary">~{etaS}s left</span>;
                      }
                    } catch { /* ignore */ }
                    return null;
                  })()}
                  <span>{pct}%</span>
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${pct}%` }}
                  data-testid="research-progress-bar"
                />
              </div>
              {isRunning && (
                <div className="text-[10px] text-muted-foreground italic">
                  Enriching ~5s per truck (8 in parallel). Rows appear below as they finish.
                </div>
              )}
            </div>

            {/* Filter chips */}
            {rows.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap pt-1">
                {FILTER_CHIPS.map((c) => {
                  const n = c.key === "all"
                    ? rows.length
                    : (counts[c.key] || 0);
                  const active = filter === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setFilter(c.key)}
                      className={`text-[11px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-transparent text-foreground border-border hover:bg-muted"
                      }`}
                      data-testid={`research-filter-${c.key}`}
                    >
                      {c.label} <span className="opacity-70">({n})</span>
                    </button>
                  );
                })}
                {counts.rejected > 0 && (
                  <span className="text-[10px] text-muted-foreground ml-2 italic">
                    {counts.rejected} rejected (excluded from downloads)
                  </span>
                )}
              </div>
            )}
          </section>
        )}

        {/* Results table */}
        {visibleRows.length > 0 && (
          <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="research-results-table">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2 text-left w-8"></th>
                    <th className="px-2 py-2 text-left">Truck</th>
                    <th className="px-2 py-2 text-left">Email</th>
                    <th className="px-2 py-2 text-left">Website</th>
                    <th className="px-2 py-2 text-left">Socials</th>
                    <th className="px-2 py-2 text-left">Phone</th>
                    <th className="px-2 py-2 text-left">Contact / Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visibleRows.map((r) => (
                    <ResultRow key={r.id} row={r} onPatch={patchRow} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Recent-jobs sidebar */}
        {recentJobs.length > 0 && !job && (
          <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
            <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2">
              Recent jobs
            </div>
            <ul className="divide-y divide-border">
              {recentJobs.map((j) => (
                <li key={j.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{j.filename}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(j.created_at).toLocaleString()} · {j.row_count} trucks · {j.status}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Warning className="h-3 w-3 text-muted-foreground" />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setJob(j); startPolling(j.id); }}
                      data-testid={`research-open-job-${j.id.slice(0, 8)}`}
                    >
                      Open
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!job && recentJobs.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-12">
            No enrichment jobs yet. Upload a CSV above to start.
          </div>
        )}
      </main>
    </div>
  );
}
